import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FJ710 } from '../../services/c209Api';
import { useC209Mode } from './hooks/useC209Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useC209Record } from './hooks/useC209Record';
import { useC209Navigation } from './hooks/useC209Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2SampleSpecs from './components/Tab2SampleSpecs';
import Tab3Remarks from './components/Tab3Remarks';
import Tab4ProgressGrid from './components/Tab4ProgressGrid';
import Tab5ColorGrid from './components/Tab5ColorGrid';
import ActionBar from './components/ActionBar';
import { getStatusBadge } from './helpers';
import './C209Form.css';

const TAB_LABELS = ['基本資料', '來樣規格與加工需求', '備註', '開發進度', '顏色明細'];

const C209Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useModulePermission(Modules.C209);
  const { mode, setMode, isEditing } = useC209Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, progresses, colors,
    selectedProgressRow, selectedColorRow, loading,
    setSelectedProgressRow, setSelectedColorRow,
    setProgresses, setColors,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addProgressRow, deleteProgressRow,
    addColorRow, deleteColorRow,
    revertToRecord, resetForAdd, applyResult,
    clearFieldErrors,
  } = useC209Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useC209Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load C209 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions [T-F07: dual-state buttons]
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FJ710) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fj710_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除申請單 ${record.fj710_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => { navTo(idx, isEditing); }, [navTo, isEditing]);
  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能待實作');
  }, [record, isEditing]);

  const statusBadge = getStatusBadge(record);

  return (
    <PermissionGuard module={Modules.C209} action="query">
    <div className="c209-form">
      {loading && <div className="loading-overlay">載入中...</div>}
      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">新布種開發申請單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>C209-新布種開發申請單</h1>
            <span className="badge badge-default">C209</span>
            {statusBadge && <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>}
          </div>
        </div>

        {/* Search Bar (BR-F24) */}
        <div className="search-bar">
          <label>快速查詢</label>
          <input
            type="text"
            placeholder="輸入申請單號"
            disabled={isEditing}
            style={{ width: 160 }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  await loadRecordWithNames(val);
                  const idx = allIds.indexOf(val);
                  if (idx >= 0) setCurrentIdx(idx);
                }
              }
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>
            {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
          </span>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {TAB_LABELS.map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo formData={formData} names={names} isEditing={isEditing}
              getField={getField} setField={setField} resolveSingleName={resolveSingleName} />
          </div>
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2SampleSpecs formData={formData} isEditing={isEditing}
              getField={getField} setField={setField} />
          </div>
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Remarks formData={formData} names={names} isEditing={isEditing}
              getField={getField} setField={setField} resolveSingleName={resolveSingleName} />
          </div>
          <div className={`tab-pane ${activeTab === 3 ? 'active' : ''}`}>
            <Tab4ProgressGrid
              progresses={progresses} selectedProgressRow={selectedProgressRow}
              isEditing={isEditing} setSelectedProgressRow={setSelectedProgressRow}
              setProgresses={setProgresses}
              addProgressRow={addProgressRow} deleteProgressRow={deleteProgressRow} />
          </div>
          <div className={`tab-pane ${activeTab === 4 ? 'active' : ''}`}>
            <Tab5ColorGrid
              colors={colors} selectedColorRow={selectedColorRow}
              isEditing={isEditing} setSelectedColorRow={setSelectedColorRow}
              setColors={setColors}
              addColorRow={addColorRow} deleteColorRow={deleteColorRow} />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) [T-F08] */}
        <ActionBar
          mode={mode} isEditing={isEditing} record={record}
          currentIdx={currentIdx} allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction} onModify={handleModifyAction}
          onDelete={handleDeleteAction} onPrint={handlePrint}
          onNavTo={handleNavTo} onExit={() => navigate('/erp')}
        />
      </div>
    </div>
    </PermissionGuard>
  );
};

export default C209Form;
